/**
 * LinkedDatabasesPickerDialog
 *
 * Sub-dialog opened from the Database Blueprint Setup dialog (edit mode) to manage
 * the set of linked databases tracked by the blueprint. Blueprints the rules of
 * `LinkChipPopover` (the toolbar's chip surface) but in a more deliberate
 * dialog UI alongside the rest of the blueprint settings.
 *
 * Rules:
 *  - One row per link with a radio that flips the active link.
 *  - Trash on the active link with 2+ links remaining surfaces an
 *    `AlertDialog` confirm before removing (blueprints `LinkChipPopover`).
 *  - Trash on the only remaining link is disabled — a blueprint must always
 *    keep ≥1 linked database.
 *  - Removing the active link auto-promotes the next link as the new active
 *    link so the toolbar's persisted active label never dangles.
 *  - Add opens the existing `AddLinkedDbDialog` nested inside.
 *  - `onChange` fires after a successful switch / add / remove so the parent
 *    can re-read the on-disk metadata (the dialog reflects external state).
 */

import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/toaster';
import { ConnectionEngineIcon } from '../../connections/components/ConnectionEngineIcon';
import { AddLinkedDbDialog } from '../../workspace/database-blueprint/components/AddLinkedDbDialog';
import { useScriptBrowserStore } from '../../script-browser/store/scriptBrowserStore';
import { removeBlueprintLink } from '../services/databaseBlueprintApi';
import type { MountedFolder } from '../../script-browser/types/scriptBrowserTypes';
import type { LinkedDb } from '../types/databaseBlueprintTypes';

export type LinkedDatabasesPickerDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folderId: string;
  folderPath: string;
  links: ReadonlyArray<LinkedDb>;
  activeLinkLabel: string;
  /** Called after a successful switch / add / remove so the parent can re-read metadata. */
  onChange?: () => void;
};

export function LinkedDatabasesPickerDialog({
  open,
  onOpenChange,
  folderId,
  folderPath,
  links,
  activeLinkLabel,
  onChange,
}: LinkedDatabasesPickerDialogProps) {
  const updateBlueprintActiveLink = useScriptBrowserStore((s) => s.updateBlueprintActiveLink);
  const updateBlueprintMetadata = useScriptBrowserStore((s) => s.updateBlueprintMetadata);

  /** While a switch is in flight we disable BOTH radios so the user can't
   *  flap between links and confuse the optimistic update. `null` = idle. */
  const [switchingTo, setSwitchingTo] = useState<string | null>(null);
  /** Removing the *active* link prompts; non-active links remove inline.
   *  `null` keeps the AlertDialog closed; any label opens it pointed at
   *  that link. */
  const [pendingRemove, setPendingRemove] = useState<string | null>(null);
  /** Whether the nested AddLinkedDbDialog is open. */
  const [addOpen, setAddOpen] = useState(false);

  // Fire the "opened" analytic exactly once on each false → true transition
  // of `open`. Effect's dep array catches the transition without the render
  // path having to track previous-open in state (which would risk a render
  // loop if mishandled).
  useEffect(() => {
    if (!open) return;
    window.electronAPI?.analytics
      ?.track('feature:database_blueprint_linked_dbs_dialog_opened', {})
      .catch(() => {
        /* fire-and-forget */
      });
  }, [open]);

  /** Resolved active link with fallback to the first link if the persisted
   *  active label is stale / corrupted (blueprints `LinkChipPopover`). */
  const effectiveActiveLabel = useMemo(() => {
    if (links.some((l) => l.label === activeLinkLabel)) return activeLinkLabel;
    return links[0]?.label ?? '';
  }, [links, activeLinkLabel]);

  /** The link the AlertDialog confirms switching to once the active link
   *  is removed. We need a stable "next" link so the description can name
   *  it and so we know which radio to flip after the IPC succeeds. */
  const fallbackOnRemove = useMemo(() => {
    if (!pendingRemove) return undefined;
    return links.find((l) => l.label !== pendingRemove);
  }, [links, pendingRemove]);

  const onlyOne = links.length <= 1;

  /** Construct the MountedFolder shape the AddLinkedDbDialog expects from
   *  the props we already have. Required so the existing dialog can do its
   *  engine-mismatch guard + label uniqueness check without us threading
   *  the full folder through. */
  const folderForAdd: MountedFolder = useMemo(
    () => ({
      id: folderId,
      directoryPath: folderPath,
      label: '',
      mountedAt: '',
      kind: 'blueprint',
      blueprintMetadata: {
        version: 1,
        linkedDbs: links.map((l) => ({
          label: l.label,
          connectionId: l.connectionId,
          connectionDisplayName: l.connectionDisplayName,
          database: l.database,
          engine: l.engine,
          lastSyncAt: l.lastSyncAt,
        })),
        activeLinkLabel: effectiveActiveLabel || undefined,
      },
    }),
    [folderId, folderPath, links, effectiveActiveLabel],
  );

  const handleSelect = async (label: string) => {
    if (label === effectiveActiveLabel || switchingTo !== null) return;
    setSwitchingTo(label);
    const link = links.find((l) => l.label === label);
    try {
      const ok = await updateBlueprintActiveLink(folderId, label);
      if (!ok) {
        toast.error({
          title: 'Could not switch active link',
          description: `"${label}" — see logs for details.`,
        });
        return;
      }
      window.electronAPI?.analytics
        ?.track('feature:database_blueprint_linked_dbs_switch_active', {
          engine: link?.engine ?? null,
        })
        .catch(() => {
          /* fire-and-forget */
        });
      onChange?.();
    } finally {
      setSwitchingTo(null);
    }
  };

  /** Trash-click handler. Active link with siblings → AlertDialog confirm;
   *  non-active → inline remove. */
  const handleRemoveClick = (label: string) => {
    if (label === effectiveActiveLabel && links.length > 1) {
      setPendingRemove(label);
      return;
    }
    void performRemove(label);
  };

  const performRemove = async (label: string) => {
    const link = links.find((l) => l.label === label);
    const wasActive = label === effectiveActiveLabel;
    const result = await removeBlueprintLink(folderPath, label);
    if (!result.success) {
      toast.error({ title: 'Could not remove link', description: result.error });
      return;
    }
    const remainingLinks = result.metadata.linkedDbs.map((l) => ({
      label: l.label,
      connectionId: l.connectionId,
      connectionDisplayName: l.connectionDisplayName,
      database: l.database,
      engine: l.engine,
      lastSyncAt: l.lastSyncAt,
    }));
    updateBlueprintMetadata(folderId, { linkedDbs: remainingLinks });
    // Auto-promote the next link to active if the removed link was active.
    if (wasActive) {
      const next = remainingLinks[0];
      if (next) {
        await updateBlueprintActiveLink(folderId, next.label);
      }
    }
    window.electronAPI?.analytics
      ?.track('feature:database_blueprint_linked_dbs_removed', {
        engine: link?.engine ?? null,
        was_active: wasActive,
      })
      .catch(() => {
        /* fire-and-forget */
      });
    toast.success({ title: `Removed link "${label}"` });
    onChange?.();
  };

  const handleConfirmRemove = async () => {
    const label = pendingRemove;
    if (!label) return;
    setPendingRemove(null);
    await performRemove(label);
  };

  const handleAddClick = () => {
    window.electronAPI?.analytics
      ?.track('feature:database_blueprint_linked_dbs_add_clicked', {})
      .catch(() => {
        /* fire-and-forget */
      });
    setAddOpen(true);
  };

  /** When the Add dialog closes, fire onChange (best-effort — the dialog
   *  updates the script-browser store itself on success, but the parent
   *  setup dialog still needs to re-read metadata to refresh the row count). */
  const handleAddOpenChange = (next: boolean) => {
    setAddOpen(next);
    if (!next) onChange?.();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-md"
          data-testid="database-blueprint-linked-dbs-dialog"
        >
          <DialogHeader>
            <DialogTitle>Linked databases</DialogTitle>
            <DialogDescription>
              Manage the databases this blueprint tracks. The active link is used by
              Refresh-from-DB, Apply, and the Database Blueprint toolbar.
            </DialogDescription>
          </DialogHeader>

          <div
            className="divide-y rounded-sm border bg-muted/20"
            data-testid="database-blueprint-linked-dbs-list"
          >
            {links.map((link) => {
              const isActive = link.label === effectiveActiveLabel;
              const switchDisabled = switchingTo !== null;
              const trashDisabled = onlyOne;
              return (
                <div
                  key={link.label}
                  className={`flex items-center gap-3 px-3 py-2 text-sm ${
                    isActive ? 'bg-accent/40' : ''
                  }`}
                  data-testid={`database-blueprint-linked-dbs-row-${link.label}`}
                >
                  <input
                    type="radio"
                    name="database-blueprint-linked-dbs-active"
                    checked={isActive}
                    disabled={switchDisabled}
                    onChange={() => {
                      void handleSelect(link.label);
                    }}
                    className="h-4 w-4 shrink-0 cursor-pointer disabled:cursor-not-allowed"
                    aria-label={`Switch active link to ${link.label}`}
                    data-testid={`database-blueprint-linked-dbs-radio-${link.label}`}
                  />
                  <ConnectionEngineIcon engine={link.engine} size="sm" iconOnly />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium truncate">{link.label}</span>
                      {isActive && (
                        <span
                          className="rounded-sm bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary"
                          data-testid={`database-blueprint-linked-dbs-active-badge-${link.label}`}
                        >
                          Active
                        </span>
                      )}
                    </div>
                    <span className="truncate text-xs text-muted-foreground">
                      {link.connectionDisplayName} · {link.database}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveClick(link.label)}
                    disabled={trashDisabled}
                    className="opacity-60 hover:opacity-100 focus:opacity-100 text-muted-foreground hover:text-destructive disabled:hover:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-30 p-1"
                    aria-label={`Remove linked database ${link.label}`}
                    title={
                      trashDisabled
                        ? 'A blueprint must keep at least one linked database.'
                        : 'Remove link'
                    }
                    data-testid={`database-blueprint-linked-dbs-remove-${link.label}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>

          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddClick}
              data-testid="database-blueprint-linked-dbs-add"
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Add linked database…
            </Button>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              data-testid="database-blueprint-linked-dbs-close"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={pendingRemove !== null}
        onOpenChange={(o) => {
          if (!o) setPendingRemove(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingRemove
                ? `Remove active link "${pendingRemove}"?`
                : 'Remove active link?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {fallbackOnRemove
                ? `This deletes the baseline cache and per-link MetaInfo for "${pendingRemove}", and switches the active link to "${fallbackOnRemove.label}".`
                : 'This deletes the baseline cache and per-link MetaInfo for this link.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                void handleConfirmRemove();
              }}
              data-testid="database-blueprint-linked-dbs-remove-confirm"
            >
              {fallbackOnRemove
                ? `Remove & switch to "${fallbackOnRemove.label}"`
                : 'Remove link'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AddLinkedDbDialog
        open={addOpen}
        folder={folderForAdd}
        onOpenChange={handleAddOpenChange}
      />
    </>
  );
}
